import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CircularProgress from '@mui/material/CircularProgress';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineContent from '@mui/lab/TimelineContent';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import api from '../api';

const DeviceInfoDialog = ({ open, onClose, devEui, isMatchCodesPage = false, onQcUpdate }) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [testResults, setTestResults] = useState([]);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isLoadingTests, setIsLoadingTests] = useState(false);


  const onError = (err)=>{
    window.showToast(err, 'error');
  }
  
  const onSuccess = (msg)=>{
    window.showToast(msg, 'success');
  }
  
  const onWarning = (msg)=>{
    window.showToast(msg, 'warning');
  }

  const fetchTestResults = async (devEui) => {
    setIsLoadingTests(true);
    try {
      const response = await api.get(`/device-test-results/${devEui}`, {
        headers: {
          "Access-Control-Allow-Origin": "*",
          Authorization: "Bearer " + localStorage.getItem("jwt"),
        }
      });
      setTestResults(response.data);
    } catch (error) {
      onError("Error fetching test results: " + error.message);
    } finally {
      setIsLoadingTests(false);
    }
  };

  const fetchDeviceDetails = async () => {
    if (!devEui) return;
    
    setIsLoadingDetails(true);
    try {
      const [deviceResponse] = await Promise.all([
        api.get(`/device-details/${devEui}`, {
          headers: {
            "Access-Control-Allow-Origin": "*",
            Authorization: "Bearer " + localStorage.getItem("jwt"),
          }
        }),
        fetchTestResults(devEui)
      ]);
      console.log('Device Details:', deviceResponse.data);
      setSelectedDevice(deviceResponse.data);
    } catch (error) {
      onError("Error fetching device details: " + error.message);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  useEffect(() => {
    if (open && devEui) {
      fetchDeviceDetails();
    }
  }, [open, devEui]);

  const handleClose = () => {
    setSelectedTab(0);
    setSelectedDevice(null);
    onClose();
  };

  const handleQualityControl = async (status) => {
    try {
      const response = await api.post(`/update-quality-control/${devEui}`, 
        { quality_control: status },
        {
          headers: {
            "Access-Control-Allow-Origin": "*",
            Authorization: "Bearer " + localStorage.getItem("jwt"),
          }
        }
      );
      
      if (response.data.device) {
        setSelectedDevice(response.data.device);
      }
      onSuccess(`Quality control ${status ? 'approved' : 'rejected'}`);
      if (onQcUpdate) {
        onQcUpdate();
      }
    } catch (error) {
      onError("Error updating quality control: " + error.message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ pb: 0 }}>
        <Box>
          <Typography variant="h6" component="div">
            {selectedDevice?.model?.model_name ? selectedDevice?.dev_eui.slice(-6) + ' - ' + selectedDevice?.model?.model_name  : selectedDevice?.dev_eui.slice(-6) || 'Loading...'}
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            mt: 0.5,
            mb: 1,
            '& .MuiTypography-root:not([class*="qc-status"])': {
              fontSize: '0.75rem',
              color: 'text.secondary'
            }
          }}>
            <Typography>
              QR: {selectedDevice?.qr_code || 'Not Matched'}
            </Typography>
            <Typography>
              MFG: {selectedDevice?.mfg_date ? 
                new Date(selectedDevice.mfg_date).toLocaleDateString() : 
                'N/A'
              }
            </Typography>
            <Typography 
              className="qc-status"
              sx={{ 
                fontSize: '0.75rem',
                color: theme => {
                  if (selectedDevice?.quality_control === true) return theme.palette.success.main;
                  if (selectedDevice?.quality_control === false) return theme.palette.error.main;
                  return theme.palette.warning.main; // null durumu için
                },
                fontWeight: 'medium'
              }}
            >
              QC: {selectedDevice?.quality_control === true ? 'Approved' : 
                   selectedDevice?.quality_control === false ? 'Rejected' : 
                   'Pending'}
            </Typography>
          </Box>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Tabs
        value={selectedTab}
        onChange={(_, newValue) => setSelectedTab(newValue)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Device Info" />
        <Tab label="Test Results" />
      </Tabs>
      <DialogContent dividers>
        {isLoadingDetails || isLoadingTests ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '200px' 
          }}>
            <CircularProgress />
          </Box>
        ) : selectedDevice && (
          <>
            {selectedTab === 0 ? (
              <Box sx={{
                p: 1,
                '& .MuiTypography-root': { mb: 0.5 }
              }}>
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: 0.5,
                  '& .MuiTypography-subtitle2': {
                    display: 'flex',
                    alignItems: 'center',
                    color: 'text.secondary'
                  }
                }}>
                  <Typography variant="subtitle2">Device EUI:</Typography>
                  <Typography>{selectedDevice.dev_eui || 'N/A'}</Typography>
                  
                  <Typography variant="subtitle2">Last Seen:</Typography>
                  <Typography sx={{
                    color: selectedDevice && selectedDevice.chirpstack && selectedDevice.chirpstack.lastSeenAt && selectedDevice.chirpstack.lastSeenAt !== 'NAN'? 'success.main' : 'error.main',
                    fontWeight: selectedDevice.qr_code ? 'normal' : 'bold' 
                  }}>
                    {selectedDevice && selectedDevice.chirpstack && selectedDevice.chirpstack.lastSeenAt && selectedDevice.chirpstack.lastSeenAt !== 'NAN'
                      ? new Date(selectedDevice.chirpstack.lastSeenAt).toLocaleString()
                      : 'Never'}
                  </Typography>
                  
                  <Typography variant="subtitle2">Frame Count Up:</Typography>
                  <Typography>{selectedDevice.chirpstack?.fCntUp || 0}</Typography>
                  
                  <Typography variant="subtitle2">QR Code:</Typography>
                  <Typography
                  sx={{
                    color: selectedDevice.qr_code ? 'success.main' : 'error.main',
                    fontWeight: selectedDevice.qr_code ? 'normal' : 'bold' 
                  }}
                  >{selectedDevice.qr_code || 'Not Matched'}</Typography>
                  
                  <Typography variant="subtitle2">Firmware:</Typography>
                  <Typography>{selectedDevice.firmware?.name || 'N/A'} ({selectedDevice.firmware?.version || 'N/A'})</Typography>
                  
                  <Typography variant="subtitle2">Batch:</Typography>
                  <Typography>{selectedDevice.batch?.name || 'N/A'}</Typography>
                  
                  <Typography variant="subtitle2">Status:</Typography>
                  <Typography sx={{
                    color: selectedDevice.chirpstack?.isActive ? 'success.main' : 'error.main',
                    fontWeight: selectedDevice.chirpstack?.isActive ? 'normal' : 'bold' 
                  }}>
                    {selectedDevice.chirpstack?.isActive ? 'Active' : 'Inactive'}
                  </Typography>
                  
                  <Typography variant="subtitle2">Signal Margin:</Typography>
                  <Typography>{selectedDevice.chirpstack?.margin || 'N/A'} dB</Typography>
                  
                </Box>
              </Box>
            ) : (
              <Box sx={{ p: 2 }}>
                {testResults.length > 0 ? (
                  testResults.map((test, index) => (
                    <Box key={index} sx={{ mb: 3 }}>
                      <Timeline sx={{ 
                        [`& .MuiTimelineItem-root`]: { 
                          minHeight: 'auto',
                          '&:before': {
                            display: 'none'
                          }
                        },
                        p: 0,
                        m: 0
                      }}>
                        {test.test_step && (
                          <TimelineItem>
                            <TimelineSeparator sx={{ minHeight: 'auto' }}>
                              <TimelineDot 
                                color={test.test_result ? 'success' : 'error'}
                                sx={{ 
                                  m: 0,
                                  p: '4px'
                                }}
                              >
                                {test.test_result ? 
                                  <CheckCircleIcon fontSize="small" /> : 
                                  <ErrorIcon fontSize="small" />
                                }
                              </TimelineDot>
                            </TimelineSeparator>
                            <TimelineContent sx={{ 
                              py: '0px', 
                              px: 2,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {test.test_step.step_name}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: test.test_result ? 'success.main' : 'error.main',
                                  fontWeight: 500
                                }}
                              >
                                {test.test_result ? 'Pass' : 'Fail'}
                              </Typography>
                            </TimelineContent>
                          </TimelineItem>
                        )}
                      </Timeline>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No test results available
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {selectedDevice?.quality_control !== true && (
          <>
            {selectedDevice?.quality_control === null && (
              <>
                <Button 
                  onClick={() => handleQualityControl(true)}
                  variant="contained" 
                  color="success"
                  sx={{ mr: 1 }}
                >
                  Approve QC
                </Button>
                <Button 
                  onClick={() => handleQualityControl(false)}
                  variant="contained" 
                  color="error"
                  sx={{ mr: 1 }}
                >
                  Reject QC
                </Button>
              </>
            )}
            {selectedDevice?.quality_control === false && (
              <Button 
                onClick={() => handleQualityControl(true)}
                variant="contained" 
                color="success"
                sx={{ mr: 1 }}
              >
                Approve QC
              </Button>
            )}
          </>
        )}
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeviceInfoDialog; 